import { useCallback, useEffect, useState } from "react";
import { showNotification } from "services/NotificationService";
import {
  buildClientOptionsForPopulation,
  buildPawGroupPayload,
  getClientLabelsForCustomer,
  getClientsForCustomer,
  getPawLoadErrorMessage,
  getPopulationTypeOptions,
  parseClientCustomerFilters,
} from "./PawLoad.utils";
import { ALL_CLIENT_OPTION } from "./PawLoad.constants";
import { createPawGroup, fetchClientCustomerFilters } from "./PawLoadService";

const usePawLoadForm = () => {
  const [filterSelectMap, setFilterSelectMap] = useState({});
  const [filterSelectMapOriginal, setFilterSelectMapOriginal] = useState({});
  const [clientDetailsMap, setClientDetailsMap] = useState({});

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedPopulationType, setSelectedPopulationType] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [populationTypeOptions, setPopulationTypeOptions] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);

  const [isLoadingFilters, setIsLoadingFilters] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadStatusMessage, setLoadStatusMessage] = useState("");
  const [loadStatusType, setLoadStatusType] = useState(null);

  const getClientsForSelectedCustomer = useCallback(
    (customerOption) => {
      if (!customerOption) {
        return [];
      }

      const clientLabelsForCustomer = getClientLabelsForCustomer(
        filterSelectMap,
        customerOption.label
      );

      return getClientsForCustomer(
        filterSelectMapOriginal.clientNameAbbreviation,
        clientLabelsForCustomer
      );
    },
    [filterSelectMap, filterSelectMapOriginal]
  );

  const loadFilters = useCallback(async () => {
    setIsLoadingFilters(true);
    try {
      const data = await fetchClientCustomerFilters();
      if (data?.filters) {
        const { filterSelectMap: nextFilterSelectMap, clientDetailsMap: nextClientDetailsMap } =
          parseClientCustomerFilters(data.filters);
        setFilterSelectMap(nextFilterSelectMap);
        setFilterSelectMapOriginal(nextFilterSelectMap);
        setClientDetailsMap(nextClientDetailsMap);
      }
    } catch (error) {
      showNotification({
        title: "Error fetching filter values!",
        message: "Something went wrong while loading customers and clients.",
        position: "tr",
        type: "error",
      });
    } finally {
      setIsLoadingFilters(false);
    }
  }, []);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  const handleCustomerChange = useCallback(
    (option) => {
      setLoadStatusMessage("");
      setLoadStatusType(null);
      setSelectedCustomer(option);
      setSelectedPopulationType(null);
      setSelectedClient(null);
      setClientOptions([]);

      if (!option) {
        setPopulationTypeOptions([]);
        return;
      }

      const clientsForCustomer = getClientsForSelectedCustomer(option);
      setPopulationTypeOptions(
        getPopulationTypeOptions(clientsForCustomer, clientDetailsMap)
      );
    },
    [clientDetailsMap, getClientsForSelectedCustomer]
  );

  const handlePopulationTypeChange = useCallback(
    (option) => {
      setLoadStatusMessage("");
      setLoadStatusType(null);
      setSelectedPopulationType(option);
      setSelectedClient(null);

      if (!option || !selectedCustomer) {
        setClientOptions([]);
        return;
      }

      const clientsForCustomer = getClientsForSelectedCustomer(selectedCustomer);
      const nextClientOptions = buildClientOptionsForPopulation(
        clientsForCustomer,
        clientDetailsMap,
        option.value
      );

      setClientOptions(nextClientOptions);
      setSelectedClient(ALL_CLIENT_OPTION);
    },
    [clientDetailsMap, getClientsForSelectedCustomer, selectedCustomer]
  );

  const handleClientChange = useCallback((option) => {
    setLoadStatusMessage("");
    setLoadStatusType(null);
    setSelectedClient(option);
  }, []);

  const handleLoad = useCallback(async () => {
    if (!selectedCustomer || !selectedPopulationType || !selectedClient) {
      return;
    }

    const payload = buildPawGroupPayload(
      selectedCustomer,
      selectedPopulationType,
      selectedClient
    );

    setIsSubmitting(true);
    setLoadStatusMessage("");
    setLoadStatusType(null);
    try {
      const response = await createPawGroup(payload);
      setLoadStatusMessage(response);
      setLoadStatusType("success");
    } catch (error) {
      setLoadStatusMessage(
        getPawLoadErrorMessage(
          error,
          selectedCustomer,
          selectedPopulationType,
          selectedClient
        )
      );
      setLoadStatusType("error");
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedClient, selectedCustomer, selectedPopulationType]);

  const isLoadEnabled =
    Boolean(selectedCustomer && selectedPopulationType && selectedClient) &&
    !isLoadingFilters;

  return {
    customerOptions: filterSelectMap.customerNameAbbreviation || [],
    populationTypeOptions,
    clientOptions,
    selectedCustomer,
    selectedPopulationType,
    selectedClient,
    isLoadingFilters,
    isSubmitting,
    isLoadEnabled,
    loadStatusMessage,
    loadStatusType,
    handleCustomerChange,
    handlePopulationTypeChange,
    handleClientChange,
    handleLoad,
  };
};

export default usePawLoadForm;
